import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import MapView from "../components/MapView";
import VideoPlayerModal from "../components/VideoPlayerModal";
import ElevationProfile from "../components/ElevationProfile";
import {
  Path,
  Mountains,
  MapPin,
  ClockCounterClockwise,
  Play,
  ArrowLeft,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const UNKNOWN_CREATOR = "__unknown_creator__";
const PAGE_SIZE = 3;
const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const fmtDistanceKm = (distanceKm) => {
  const value = Number(distanceKm);
  if (!Number.isFinite(value) || value <= 0) return "- km";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toLocaleString()} km`;
};

const fmtElevationM = (elevationM) => {
  const value = Number(elevationM);
  if (!Number.isFinite(value) || value <= 0) return "- m";
  return `${Math.round(value).toLocaleString()} m`;
};

function PlaylistGallery({ group, filterAndSort, onSelectVideo }) {
  const [offset, setOffset] = useState(0);
  const containerRef = useRef(null);
  const videos = filterAndSort(group.videos);
  const total = videos.length;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  // Reset offset when the filtered list shrinks (e.g. creator filter change)
  useEffect(() => {
    setOffset((o) =>
      o + PAGE_SIZE > total && total > 0
        ? Math.max(0, total - PAGE_SIZE)
        : o === 0
          ? 0
          : Math.min(o, Math.max(0, total - PAGE_SIZE)),
    );
  }, [total]);

  const prev = () => setOffset((o) => Math.max(0, o - 1));
  const next = () => setOffset((o) => Math.min(o + 1, total - PAGE_SIZE));

  // Arrow keys when the gallery container is focused
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  // Horizontal mousewheel (or trackpad horizontal swipe)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return; // ignore vertical scroll
      e.preventDefault();
      if (e.deltaX > 0) next();
      else prev();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="outline-none focus-visible:ring-2 focus-visible:ring-[#C86B4E] rounded-xl"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-xl">{group.title}</h3>
          {(group.creator_name || group.creator_url) && (
            <div className="text-xs text-white/70 mt-1 flex items-center gap-3">
              {group.creator_avatar && (
                <img
                  src={group.creator_avatar}
                  alt={group.creator_name || "Creator avatar"}
                  className="w-6 h-6 rounded-full object-cover"
                />
              )}
              <div>
                {group.creator_name ? `By ${group.creator_name}` : ""}
                {group.creator_name && group.creator_url ? " · " : ""}
                {group.creator_url && (
                  <a
                    href={group.creator_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-white"
                  >
                    Creator website
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/50">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button
              onClick={prev}
              disabled={!canPrev}
              aria-label="Previous video"
              className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <button
              onClick={next}
              disabled={!canNext}
              aria-label="Next video"
              className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* overflow wrapper clips the sliding track */}
      <div className="mt-4 overflow-hidden">
        <div
          className="flex gap-6 transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(calc(-${offset} * (100% / ${PAGE_SIZE}) - ${offset} * (24px / ${PAGE_SIZE})))`,
          }}
        >
          {videos.map((v) => (
            <button
              key={v.id}
              onClick={() => onSelectVideo(v)}
              data-testid={`video-card-${v.id}`}
              className="flex-none w-[calc((100%-(2*24px))/3)] text-left group bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl overflow-hidden transition-colors"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={v.thumbnail}
                  alt={v.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-[#C86B4E] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={22} weight="fill" className="text-white ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded">
                  {fmt(v.duration)}
                </div>
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C86B4E]">
                  {v.creator}
                </div>
                <h3 className="font-display font-bold text-lg mt-1">
                  {v.title}
                </h3>
                <div className="text-xs text-white/60 mt-2">
                  {(v.pois || []).length} POIs · {fmtDistanceKm(v.distance_km)}{" "}
                  · {fmtElevationM(v.elevation_m)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function RouteDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(null);
  const [hoveredElevationIndex, setHoveredElevationIndex] = useState(null);
  const [authorFilter, setAuthorFilter] = useState("All");
  const [sortAsc, setSortAsc] = useState(true);
  const requestedVideoID = searchParams.get("video") || "";
  const requestedPlaylistID = searchParams.get("playlist") || "";

  useEffect(() => {
    api.get(`/routes/${id}`).then(({ data }) => setData(data));
  }, [id]);

  const allVideos = useMemo(() => {
    if (!data) return [];
    // combine top-level videos and playlist videos into one flat list for filtering
    const top = data.videos || [];
    const grouped = (data.playlists || data.video_groups || []).flatMap(
      (g) => g.videos || [],
    );
    // some videos may appear in both; dedupe by id
    const map = new Map();
    top.concat(grouped).forEach((v) => map.set(v.id, v));
    return Array.from(map.values());
  }, [data]);

  const creators = useMemo(() => {
    const set = new Set();
    allVideos.forEach((v) => set.add(v.creator || ""));
    return [
      "All",
      ...Array.from(set)
        .filter((s) => s && s.length > 0)
        .sort(),
    ];
  }, [allVideos]);

  useEffect(() => {
    if (!requestedVideoID || allVideos.length === 0) return;
    const target = allVideos.find((v) => v.id === requestedVideoID);
    if (target) setActiveVideo(target);
  }, [requestedVideoID, allVideos]);

  useEffect(() => {
    if (activeGalleryIndex == null) return;

    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveGalleryIndex((prev) => {
          if (prev == null || !data?.route?.gallery?.length) return prev;
          const total = data.route.gallery.length;
          return (prev - 1 + total) % total;
        });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setActiveGalleryIndex((prev) => {
          if (prev == null || !data?.route?.gallery?.length) return prev;
          const total = data.route.gallery.length;
          return (prev + 1) % total;
        });
      } else if (e.key === "Escape") {
        setActiveGalleryIndex(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeGalleryIndex, data]);

  const filterAndSort = (videos) => {
    let out = (videos || []).slice();
    if (authorFilter && authorFilter !== "All") {
      out = out.filter((v) => v.creator === authorFilter);
    }
    out.sort((a, b) => {
      const A = (a.creator || "").toLowerCase();
      const B = (b.creator || "").toLowerCase();
      if (A === B) return 0;
      return sortAsc ? (A < B ? -1 : 1) : A > B ? -1 : 1;
    });
    return out;
  };

  const renderVideosGrid = (videos) => {
    const list = filterAndSort(videos);
    if (!list || list.length === 0) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {list.map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveVideo(v)}
            data-testid={`video-card-${v.id}`}
            className="text-left group bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl overflow-hidden transition-colors"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={v.thumbnail}
                alt={v.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-[#C86B4E] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={22} weight="fill" className="text-white ml-1" />
                </div>
              </div>
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded">
                {fmt(v.duration)}
              </div>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C86B4E]">
                {v.creator}
              </div>
              <h3 className="font-display font-bold text-lg mt-1">{v.title}</h3>
              <div className="text-xs text-white/60 mt-2">
                {(v.pois || []).length} POIs · {fmtDistanceKm(v.distance_km)} ·{" "}
                {fmtElevationM(v.elevation_m)}
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  // Keep hooks before any early return.
  const overviewPoints = data?.route?.gpx_points || [];
  const center = overviewPoints.length > 0 ? overviewPoints[0] : [0, 0];
  const highlightedElevationPosition = useMemo(() => {
    if (hoveredElevationIndex == null) return null;
    const p = overviewPoints[hoveredElevationIndex];
    if (!Array.isArray(p) || p.length < 2) return null;
    return [p[0], p[1]];
  }, [hoveredElevationIndex, overviewPoints]);

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C665F]">
        Loading…
      </div>
    );

  const { route, videos = [] } = data;
  const playlists = data.playlists || data.video_groups || [];
  const visiblePlaylists = requestedPlaylistID
    ? playlists.filter((g) => g.id === requestedPlaylistID)
    : playlists;

  const unescapeHTMLDOM = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return doc.documentElement.textContent;
  };
  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="route-detail-page">
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img
          src={route.hero_image}
          alt={route.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D1A] via-[#1A1D1A]/30 to-transparent" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-8 pb-16 flex flex-col justify-end">
          <Link
            to="/routes"
            className="text-white/80 hover:text-white text-sm font-bold inline-flex items-center gap-2 mb-6 self-start"
            data-testid="back-to-routes"
          >
            <ArrowLeft size={16} /> All routes
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E] flex items-center gap-2">
            <MapPin size={14} weight="fill" /> {route.location}, {route.country}
          </div>
          <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl mt-3 max-w-4xl leading-[0.95]">
            {route.name}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Left: details */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {[
              { i: Path, l: "Distance", v: `${route.distance_km} km` },
              {
                i: Mountains,
                l: "Elevation",
                v: `${route.elevation_m.toLocaleString()} m`,
              },
              { i: ClockCounterClockwise, l: "Days", v: route.days },
              { i: MapPin, l: "Difficulty", v: route.difficulty },
            ].map(({ i: Icon, l, v }) => (
              <div
                key={l}
                className="bg-white border border-[#E5E0D8] rounded-2xl p-4"
              >
                <Icon size={20} className="text-[#2C5545]" weight="duotone" />
                <div className="text-[10px] uppercase tracking-wider text-[#5C665F] font-bold mt-2">
                  {l}
                </div>
                <div className="font-display font-black text-xl mt-1">{v}</div>
              </div>
            ))}
          </div>

          <h2 className="font-display font-bold text-2xl mb-3">
            About this route
          </h2>
          <p className="text-[#5C665F] leading-relaxed">
            {unescapeHTMLDOM(route.description)}
          </p>

          {route.external_url && (
            <a
              href={route.external_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex mt-4 rounded-full border border-[#2C5545] px-4 py-2 text-sm font-bold text-[#2C5545] hover:bg-[#2C5545] hover:text-white transition-colors"
            >
              Visit external route page
            </a>
          )}
          <button
            type="button"
            onClick={() =>
              document
                .getElementById("route-videos")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex mt-4 ml-3 rounded-full border border-[#2C5545] px-4 py-2 text-sm font-bold text-[#2C5545] hover:bg-[#2C5545] hover:text-white transition-colors"
          >
            Watch Route Videos
          </button>

          {route.gallery?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl mb-4">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {route.gallery.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveGalleryIndex(i)}
                    className="group relative overflow-hidden rounded-xl"
                    aria-label={`Open gallery image ${i + 1}`}
                  >
                    <img
                      src={img}
                      alt={`Route gallery ${i + 1}`}
                      className="rounded-xl object-cover w-full aspect-square transition-transform duration-300 group-hover:scale-[1.12] group-focus-visible:scale-[1.12]"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: overview map */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden h-[420px]">
            <MapView
              gpxPoints={overviewPoints}
              center={[center[0], center[1]]}
              highlightPosition={highlightedElevationPosition}
              height="100%"
              interactive={true}
              fit={overviewPoints.length > 0}
            />
          </div>
          <div className="text-xs text-[#5C665F] mt-2 px-2">
            All filmed segments shown on map.
          </div>
          <div className="mt-3 bg-[#1A1D1A] border border-[#2F3431] rounded-2xl overflow-hidden">
            <ElevationProfile
              gpxPoints={overviewPoints}
              onHoverIndex={setHoveredElevationIndex}
            />
          </div>
        </div>
      </div>

      {/* Videos */}
      <div id="route-videos" className="bg-[#1A1D1A] text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E]">
                The films
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3">
                {allVideos.length} {allVideos.length === 1 ? "film" : "films"}{" "}
                for this route
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <Select value={authorFilter} onValueChange={setAuthorFilter}>
                <SelectTrigger
                  className="w-[180px] h-11 rounded-full text-black bg-white border-[#E5E0D8]"
                  data-testid="creator-filter"
                >
                  <SelectValue placeholder="Creator" />
                </SelectTrigger>
                <SelectContent>
                  {creators.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c === "all"
                        ? "All creators"
                        : c === UNKNOWN_CREATOR
                          ? "Unknown creator"
                          : c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={() => setSortAsc(!sortAsc)}
                className="rounded px-3 py-2 border"
                data-testid="sort-toggle"
              >
                Sort: {sortAsc ? "A→Z" : "Z→A"}
              </button>
            </div>
          </div>

          {/* Playlist videos */}
          {visiblePlaylists.length > 0 ? (
            <div className="mt-6 space-y-8">
              {visiblePlaylists
                .filter((group) => filterAndSort(group.videos).length > 0)
                .map((group) => (
                  <PlaylistGallery
                    key={group.id}
                    group={group}
                    filterAndSort={filterAndSort}
                    onSelectVideo={setActiveVideo}
                  />
                ))}
              {/* ungrouped videos 
              {!requestedPlaylistID &&
                videos.filter((v) => !v.group_id || v.group_id === "").length >
                0 && (
                  <div>
                    <h3 className="font-display font-bold text-xl">
                      Other videos
                    </h3>
                    <div className="mt-4">
                      {renderVideosGrid(
                        videos.filter((v) => !v.group_id || v.group_id === ""),
                      )}
                    </div>
                  </div>
                )}
              */}
            </div>
          ) : requestedPlaylistID ? (
            <div className="mt-10 text-white/60">
              No playlist found for this route.
            </div>
          ) : (
            renderVideosGrid(videos)
          )}

          {videos.length === 0 && (
            <div className="mt-10 text-white/60">
              No films available yet for this route.
            </div>
          )}
        </div>
      </div>

      <VideoPlayerModal
        open={!!activeVideo}
        onOpenChange={(o) => !o && setActiveVideo(null)}
        video={activeVideo}
        route={route}
        playlists={playlists}
        onNextVideo={(nextVideo) => setActiveVideo(nextVideo)}
      />

      {activeGalleryIndex != null && route.gallery?.[activeGalleryIndex] && (
        <div
          className="fixed inset-0 z-[2200] bg-black/85 p-4 md:p-8 flex items-center justify-center"
          onClick={() => setActiveGalleryIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Expanded gallery image"
        >
          <button
            type="button"
            className="absolute top-4 right-4 rounded-full bg-white/20 hover:bg-white/35 text-white px-3 py-1 text-sm font-bold"
            onClick={() => setActiveGalleryIndex(null)}
          >
            Close
          </button>
          <button
            type="button"
            className="absolute left-3 md:left-6 rounded-full bg-white/20 hover:bg-white/35 text-white w-10 h-10 text-xl font-bold"
            onClick={(e) => {
              e.stopPropagation();
              setActiveGalleryIndex((prev) => {
                if (prev == null || !route.gallery?.length) return prev;
                return (prev - 1 + route.gallery.length) % route.gallery.length;
              });
            }}
            aria-label="Previous image"
          >
            {"<"}
          </button>
          <button
            type="button"
            className="absolute right-3 md:right-6 rounded-full bg-white/20 hover:bg-white/35 text-white w-10 h-10 text-xl font-bold"
            onClick={(e) => {
              e.stopPropagation();
              setActiveGalleryIndex((prev) => {
                if (prev == null || !route.gallery?.length) return prev;
                return (prev + 1) % route.gallery.length;
              });
            }}
            aria-label="Next image"
          >
            {">"}
          </button>
          <img
            src={route.gallery[activeGalleryIndex]}
            alt="Expanded route gallery"
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
